import { create } from 'zustand';
import type { ErrorInfo } from 'react';
import { devtools, persist } from 'zustand/middleware';
import type { PersistOptions } from 'zustand/middleware';
import type { ProfileUser, UpdateUser } from '@lingugeek/schemas';
import type { AuthState, AuthActions } from '../types';
import { getApiService } from '../api/services';
import { ApiError } from '@lingugeek/shared';
import { AuthError } from '@lingugeek/errors';

interface AuthStore extends AuthState {
  actions: AuthActions;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    devtools(
      (set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,

        // Actions
        actions: {
          login: (user: ProfileUser) => {
            set(
              {
                user,
                isAuthenticated: true,
                error: null,
              },
              false,
              'auth/login'
            );
          },

          logout: async () => {
            try {
              set({ isLoading: true }, false, 'auth/logout/start');

              const apiService = getApiService();
              await apiService.logout();

              set(
                {
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                },
                false,
                'auth/logout/success'
              );
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Logout failed';
              set(
                {
                  isLoading: false,
                  error: errorMessage,
                },
                false,
                'auth/logout/error'
              );

              // Still clear the user state even if logout API fails
              set(
                {
                  user: null,
                  isAuthenticated: false,
                },
                false,
                'auth/logout/clear'
              );
            }
          },

          updateUser: async (updates: UpdateUser) => {
            const { user } = get();
            if (!user) {
              throw new Error('No user to update');
            }

            try {
              set({ isLoading: true }, false, 'auth/updateUser/start');

              const apiService = getApiService();
              await apiService.updateUser(updates);

              const updatedProfileUser = { ...user, ...updates };
              set(
                {
                  user: updatedProfileUser,
                  isLoading: false,
                  error: null,
                },
                false,
                'auth/updateUser/success'
              );
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Update user failed';
              set(
                {
                  isLoading: false,
                  error: errorMessage,
                },
                false,
                'auth/updateUser/error'
              );
              throw error;
            }
          },

          refreshUser: async () => {
            try {
              set({ isLoading: true }, false, 'auth/refreshUser/start');

              const apiService = getApiService();
              const user = await apiService.getUser();

              set(
                {
                  user,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                },
                false,
                'auth/refreshUser/success'
              );
            } catch (error) {
              // For security: only clear auth state on definitive auth failures
              // Network errors, server errors, etc. should not clear auth state
              const shouldClearAuth =
                (error instanceof ApiError && error.statusCode === 401) ||
                error instanceof AuthError;

              if (shouldClearAuth) {
                set(
                  {
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: 'Session expired. Please log in again.',
                  },
                  false,
                  'auth/refreshUser/unauthorized'
                );
              } else {
                // Keep existing auth state for non-auth errors (network, server, etc.)
                set(
                  {
                    isLoading: false,
                    error: null, // Don't show error for network issues during refresh
                  },
                  false,
                  'auth/refreshUser/networkError'
                );
              }
              throw error;
            }
          },

          googleLogin: async (accessToken: string) => {
            try {
              set({ isLoading: true }, false, 'auth/googleLogin/start');

              const apiService = getApiService();
              const response =
                await apiService.authenticateWithGoogle(accessToken);

              // After successful Google auth, refresh user data
              const user = await apiService.getUser();

              set(
                {
                  user,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                },
                false,
                'auth/googleLogin/success'
              );

              return response;
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Google login failed';
              set(
                {
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: errorMessage,
                },
                false,
                'auth/googleLogin/error'
              );
              throw error;
            }
          },

          setLoading: (loading: boolean) => {
            set({ isLoading: loading }, false, 'auth/setLoading');
          },

          setError: (error: string | null) => {
            set({ error }, false, 'auth/setError');
          },
        },
      }),
      {
        name: 'auth-store',
      }
    ),
    {
      name: 'auth-store',
      storage: {
        getItem: (name: string) => {
          const value = sessionStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name: string, value: any) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name: string) => {
          sessionStorage.removeItem(name);
        },
      },
      partialize: (state: AuthStore) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    } satisfies PersistOptions<
      AuthStore,
      Pick<AuthStore, 'user' | 'isAuthenticated'>
    >
  )
);

// Add error boundary support to stores
export const errorBoundary = (error: Error, _errorInfo: ErrorInfo) => {
  // Clear sensitive state on errors
  useAuthStore.getState().actions.setError(error.message);
};
